import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import OtpInputBox from "./OtpInputBox";
import { useOtp } from "context/OtpContext";
import { OTP_LOGIN_ACTION } from "../../../constants/authConstants";
const { verifyOtp } = require("../../authentication/_request");
import { OTP_LENGTH } from "../../../constants/authConstants";
import MDTypography from "components/MDTypography";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context";
import MDButton from "components/MDButton";
import { maskIdentifier } from "core/utils/maskIdentifier";
import { getCustomerAPI } from "layouts/profile/_request";

const OtpInputForm = ({ length, setLevel }) => {
  const { otp, identifier, isOtpCompleted, isLoading, inputRefs, handleSendOtp, dispatch } =
    useOtp();

  const { saveAuthData, showSnackbarWithMessage } = useAuth();

  const navigate = useNavigate();

  const handleBack = (e) => {
    e.preventDefault();

    dispatch({ type: OTP_LOGIN_ACTION.BACK });
  };

  const handleChange = (e, index) => {
    const value = e.target.value;

    if (isNaN(value) || value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    dispatch({ type: OTP_LOGIN_ACTION.SET_OTP, payload: newOtp });

    if (index !== length - 1 && value) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const newOtp = [...otp];
      newOtp[index - 1] = "";
      dispatch({ type: OTP_LOGIN_ACTION.SET_OTP, payload: newOtp });

      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Filter out non-numeric characters
    const cleanedData = pastedData.replace(/\D/g, "");

    // Limit to the remaining available slots
    const remainingLength = length - index;
    const truncatedData = cleanedData.slice(0, remainingLength);

    if (!truncatedData) return;

    // Update OTP state with pasted digits
    const newOtp = [...otp];

    for (let i = 0; i < truncatedData.length; i++) {
      if (index + i < length) {
        newOtp[index + i] = truncatedData[i];
      }
    }

    dispatch({ type: OTP_LOGIN_ACTION.SET_OTP, payload: newOtp });

    // Focus on the next empty input or the last input
    const nextIndex = Math.min(index + truncatedData.length, length - 1);
    if (nextIndex < length) {
      inputRefs.current[nextIndex].focus();
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });

    let otpValue = otp.join("");

    if (otpValue.length < OTP_LENGTH || isNaN(parseInt(otpValue)) || !identifier?.trim()) {
      dispatch({ type: OTP_LOGIN_ACTION.MALFORMED_REQUEST });
      return;
    }

    const {
      data: { success, message, data },
    } = await verifyOtp({ identifier, otp: otpValue });

    if (success) {
      if (data.isNewEmail) {
        saveAuthData(data.tokens);
        setLevel(1); //level 1 is for profile details page
      } else {
        showSnackbarWithMessage({
          type: "success",
          message,
        });
        saveAuthData(data.tokens);

        const {
          data: { data: customerDetail },
        } = await getCustomerAPI();

        if (
          !customerDetail?.customer?.firstName &&
          (!customerDetail?.customer?.lastName || !customerDetail?.customer?.phoneNumber)
        ) {
          setLevel(1); //level 1 is for profile details page
        } else {
          navigate("/dashboard");
        }
      }
    } else {
      showSnackbarWithMessage({
        type: "error",
        message,
      });
    }

    dispatch({ type: OTP_LOGIN_ACTION.TOGGLE_LOADING });
  };

  return (
    <>
      {/* Email Details  */}
      <Box sx={{ mt: "-34px" }} display="flex" flexDirection="column" alignItems="center">
        <p
          style={{
            fontSize: 16,
            color: "#475467",
            lineHeight: "20px",
            textAlign: "center",
          }}
        >
          We&apos;ve sent a 6 digit code to{" "}
          <span style={{ fontWeight: 600 }}>{maskIdentifier(identifier)}</span>
        </p>

        <Box
          mt={"30px"}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="start"
        >
          <div style={{ display: "flex", gap: "12px" }}>
            {otp.map((value, index) => (
              <React.Fragment key={index}>
                <OtpInputBox
                  value={value}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={(e) => handlePaste(e, index)}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                />
              </React.Fragment>
            ))}
          </div>
          {/* Resend OTP Code  */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            style={{ marginTop: "6px" }}
          >
            <MDTypography
              color={"textSecondary"}
              sx={{ fontSize: "14px", ml: "2px" }}
              fontWeight="normal"
            >
              Didn’t get a code?
              <span
                style={{ textDecoration: "underline", cursor: "pointer", marginLeft: "3px" }}
                onClick={handleSendOtp}
              >
                Click to resend.
              </span>
            </MDTypography>
          </Box>
        </Box>
      </Box>

      {/* BACK AND NEXT BTN  */}
      <Box display="flex" gap={2} alignItems="center" style={{ marginTop: "30px" }}>
        <MDButton
          fullWidth
          onClick={handleBack}
          variant="outlined"
          color="black"
          sx={{
            borderRadius: "8px",
            padding: "10px",
            border: "1px solid #D0D5DD",
            textDecoration: "lowercase",
            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
          }}
        >
          Back
        </MDButton>

        <MDButton
          fullWidth
          variant="contained"
          color="success"
          disabled={isLoading || !isOtpCompleted}
          onClick={handleVerifyOtp}
        >
          Next
        </MDButton>
      </Box>
    </>
  );
};

OtpInputForm.propTypes = {
  length: PropTypes.number.isRequired,
  setLevel: PropTypes.any,
};

export default OtpInputForm;
